import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { isScannerQuery } from '@js/algolia/queryFilter.js';

/**
 * Alpine component for modal-based global search
 * Returns results as Alpine data for Twig/Alpine template rendering
 *
 * Usage:
 * <div x-data="globalSearchModal()">
 *   <input type="search" x-model="query" @input="search()">
 *   <button @click="setTypeFilter(null)">All</button>
 *   <template x-for="facet in facets">
 *     <button @click="setTypeFilter(facet.value)" x-text="facet.label"></button>
 *   </template>
 *   <template x-for="result in results">
 *     <a :href="result.url" x-text="result.title"></a>
 *   </template>
 * </div>
 */
export default function GlobalSearchModal() {
  return {
    query: '',
    results: [],
    facets: [],
    cachedFacets: [],
    activeType: null,
    isLoading: false,
    isInitialized: false,
    noResults: false,
    totalHits: 0,
    currentPage: 0,
    totalPages: 0,
    selectedIndex: -1,
    searchClient: null,
    debounceTimer: null,
    debounceMs: 300,
    minLength: 2,
    hitsPerPage: 10,
    facetAttribute: 'type',

    init() {
      const config = window.algoliaConfig;
      if (!config?.app_id || !config?.api_key) {
        console.warn('[GlobalSearchModal] Algolia config not found');
        return;
      }

      this.searchClient = algoliasearch(config.app_id, config.api_key);
      this.indexName = config.indices?.globalSearch || 'dev_globalSearch';
      this.siteId = config.site_id || null;
      this.isInitialized = true;
    },

    search() {
      clearTimeout(this.debounceTimer);

      // Reset if empty
      if (!this.query || this.query.length < this.minLength) {
        this.results = [];
        this.noResults = false;
        this.totalHits = 0;
        this.currentPage = 0;
        this.totalPages = 0;
        return;
      }

      // Filter scanner queries
      if (isScannerQuery(this.query)) {
        return;
      }

      this.debounceTimer = setTimeout(
        () => this.performSearch(),
        this.debounceMs,
      );
    },

    async performSearch() {
      if (!this.searchClient || !this.isInitialized) {
        this.init();
        if (!this.isInitialized) return;
      }

      this.isLoading = true;

      try {
        const filters = ["status:'live'", 'enabledForSite:true'];

        if (this.siteId) {
          filters.push(`siteId:${this.siteId}`);
        }

        // Apply type filter if active
        if (this.activeType) {
          filters.push(`${this.facetAttribute}:"${this.activeType}"`);
        }

        const params = {
          hitsPerPage: this.hitsPerPage,
          page: this.currentPage,
          facets: [this.facetAttribute],
          filters: filters.join(' AND '),
        };

        const response = await this.searchClient.search([
          {
            indexName: this.indexName,
            query: this.query,
            params,
          },
        ]);

        const result = response.results[0];
        this.results = result.hits.map((hit) => this.formatResult(hit));
        this.selectedIndex = -1;
        this.totalHits = result.nbHits;
        this.totalPages = result.nbPages;
        this.currentPage = result.page;
        this.noResults = this.results.length === 0;

        // Update facets from response
        this.updateFacets(result.facets?.[this.facetAttribute] || {});
      } catch (error) {
        console.error('[GlobalSearchModal] Search error:', error);
        this.results = [];
        this.noResults = true;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Custom facet order (lower = first)
     * Types not listed will appear after these, sorted by count
     */
    facetOrder: {
      robot: 1,
      robodrill: 2,
      cnc: 3,
      software: 4,
      resources: 10,
      pages: 99,
    },

    /**
     * Update facets from Algolia response
     * Caches facets when unfiltered to keep tabs stable during filtering
     */
    updateFacets(facetCounts) {
      const newFacets = Object.entries(facetCounts)
        .map(([value, count]) => ({
          value,
          label: this.formatFacetLabel(value),
          count,
        }))
        .sort((a, b) => {
          const orderA = this.facetOrder[a.value] ?? 50;
          const orderB = this.facetOrder[b.value] ?? 50;
          if (orderA !== orderB) return orderA - orderB;
          return b.count - a.count; // Secondary sort by count
        });

      // Cache facets when no filter is active (initial search)
      if (!this.activeType) {
        this.cachedFacets = newFacets;
        this.facets = newFacets;
      } else {
        // When filtered, use cached facets to keep all tabs visible
        this.facets = this.cachedFacets;
      }
    },

    /**
     * Format facet label for display
     */
    formatFacetLabel(value) {
      const labels = {
        cnc: 'CNC',
        robodrill: 'Robodrills',
        robot: 'Robots',
      };
      return labels[value] ?? value.charAt(0).toUpperCase() + value.slice(1);
    },

    /**
     * Set type filter and re-search
     */
    setTypeFilter(type) {
      this.activeType = type;
      this.currentPage = 0;
      if (this.query.length >= this.minLength) {
        this.performSearch();
      }
    },

    /**
     * Go to next page
     */
    nextPage() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++;
        this.performSearch();
      }
    },

    /**
     * Go to previous page
     */
    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.performSearch();
      }
    },

    /**
     * Format a hit for template rendering
     * Override this method for custom formatting
     */
    formatResult(hit) {
      const type = hit.type || hit.section || null;
      return {
        objectID: hit.objectID,
        title:
          hit.title || hit.name || this.$el.dataset.textUntitled || 'Untitled',
        url: hit.url || '#',
        description: hit.description || hit.summary || null,
        type: type ? this.formatFacetLabel(type) : null,
        image: hit.image || hit.thumbnail || null,
      };
    },

    clear() {
      this.query = '';
      this.results = [];
      this.facets = [];
      this.cachedFacets = [];
      this.activeType = null;
      this.noResults = false;
      this.totalHits = 0;
      this.currentPage = 0;
      this.totalPages = 0;
      this.selectedIndex = -1;
    },

    /**
     * Keyboard navigation: select next result (j or down arrow)
     */
    selectNext() {
      if (this.results.length === 0) return;
      this.selectedIndex = Math.min(
        this.selectedIndex + 1,
        this.results.length - 1,
      );
      this.scrollToSelected();
    },

    /**
     * Keyboard navigation: select previous result (k or up arrow)
     */
    selectPrev() {
      if (this.results.length === 0) return;
      this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
      this.scrollToSelected();
    },

    /**
     * Keyboard navigation: open selected result
     */
    openSelected() {
      if (this.selectedIndex >= 0 && this.results[this.selectedIndex]) {
        window.location.href = this.results[this.selectedIndex].url;
      }
    },

    /**
     * Scroll selected result into view
     */
    scrollToSelected() {
      this.$nextTick(() => {
        const selected = this.$el?.querySelector('[data-selected="true"]');
        selected?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      });
    },

    /**
     * Check if result at index is selected
     */
    isSelected(index) {
      return this.selectedIndex === index;
    },
  };
}
